import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { getCostRecipe, type CostRecipeState } from "@workspace/api-client-react";
import { costRecipeSchema, deriveCostRecipe, recipeForQuoteSnapshot, reconcileRecipeTypical, type CostRecipe } from "@workspace/api-zod/cost-recipe";
import { costSectionTotal, CostBreakdownView, CostBreakdownEdit, type CostSection } from "./cost-breakdown-table";
import { StackedBricksIcon, TowerCraneIcon } from "./cost-section-icons";
import { HardHat } from "lucide-react";
import type { QuantityDerivation } from "@workspace/api-zod/quantity-dimensions";
import { lineMaterialRows } from "@workspace/api-zod/materials-list";
import { ensureLengthDimensions, hasDimensionPricing, manualRecipePrice, priceRecipeFromDimensions } from "@workspace/api-zod/dimension-pricing";
import { QuoteSectionAllowance, type QuoteSplitField } from "./quote-section-allowance";
import { PurchaseMaterialSummary } from "./material-allowance-control";
import { recipeProcurementSafe, validMaterialAllowance, type MaterialAllowanceApproval } from "@workspace/api-zod/material-allowance";
import { MaterialLookupControl } from "./material-lookup-control";

/** The allocation on a quote is for the entire line; recipes are per unit. */
export function quoteRecipeSplit(recipe: CostRecipe, quantity: number) {
  const total = Math.round(Number(deriveCostRecipe(recipe).rateTypical) * quantity * 100);
  let materials = lineMaterialRows({ name: "", category: "", unit: "each", unitCost: 0, quantity, quoteCostRecipe: recipe }, 0)
    .reduce((n, row) => n + Math.round(row.exactTotal * 100), 0);
  let plant = Math.round(recipe.plant_allowance * quantity * 100);
  // A penny-rounded material row can differ from the total's aggregate rounding.
  if (materials + plant > total) materials = Math.max(0, total - plant);
  const labour = total - materials - plant;
  return { labourCost: labour / 100, materialCost: materials / 100, plantCost: plant / 100 };
}

export function recipeAtQuotePrice(recipe: CostRecipe, unitCost: number): CostRecipe {
  // Normalize before the first parser, not just before sending the request.
  recipe = recipeForQuoteSnapshot(recipe);
  if (!Number.isFinite(unitCost) || unitCost < 0 || Math.abs(Math.round(unitCost * 100) - unitCost * 100) > 0.000001)
    throw new RangeError("This item needs a valid per-unit price in pounds and pence before a detailed breakdown can be shown.");
  const price = unitCost.toFixed(2);
  return Number(deriveCostRecipe(recipe).rateTypical) === unitCost
    ? deriveCostRecipe(recipe).recipe : manualRecipePrice(reconcileRecipeTypical(recipe, price).recipe);
}

interface Item {
  name?: string;
  category?: string;
  materialAllowanceApproval?: unknown;
  description?: string;
  quantityDerivation?: QuantityDerivation | null;
  costLibraryTaskId?: string | null;
  quoteCostRecipe?: CostRecipe | null;
  quantity: number;
  unitCost: number;
  unit: string;
  labourCost?: number;
  materialCost?: number;
  plantCost?: number;
  sourceVariant?: { name?: string; specification?: string };
}

/** Count-unit aliases do not change recipe quantities; measured units still must match. */
export function recipeUnitsMatch(libraryUnit: string, quoteUnit: string): boolean {
  const canonical = (unit: string) => {
    const value = unit.trim().toLowerCase();
    return ["each", "item", "items", "no", "no.", "nr", "unit"].includes(value) ? "each" : value;
  };
  return canonical(libraryUnit) === canonical(quoteUnit);
}

export function QuoteItemRecipeBreakdown({ item, index, market, guestMode, purchasingManaged = false, onApply, onSectionChange, action, onSplitChange, symbol = "£", sessionKey, onMaterialAllowanceChange }: {
  item: Item; index: number; market: string; guestMode: boolean; purchasingManaged?: boolean;
  onApply: (recipe: CostRecipe) => void;
  onSectionChange?: (open: boolean) => void;
  action?: ReactNode;
  onSplitChange?: (field: QuoteSplitField, value: number) => void;
  symbol?: string;
  sessionKey?: string;
  onMaterialAllowanceChange?: (value: MaterialAllowanceApproval | null) => void;
}) {
  const [section, setSection] = useState<CostSection | null>(null);
  const [editing, setEditing] = useState(false);
  const taskId = item.costLibraryTaskId;
  const purchaseSnapshot = validMaterialAllowance({ ...item, name: item.name ?? "", category: item.category ?? "general" });
  const query = useQuery({
    queryKey: ["quote-item-recipe", taskId],
    enabled: (!purchasingManaged || (!!section && section !== "materials")) && !item.quoteCostRecipe && !guestMode && market === "uk" && !!taskId && taskId !== "added" && taskId !== "published",
    staleTime: 0,
    retry: false,
    queryFn: async (): Promise<CostRecipeState> => {
      try { return await getCostRecipe("default", taskId!); }
      catch (error) {
        if ((error as { status?: number }).status !== 404) throw error;
        return getCostRecipe("custom", taskId!);
      }
    },
  });
  const remoteRecipe = query.data?.recipe ? costRecipeSchema.safeParse(query.data.recipe) : null;
  const unitMismatch = query.data?.unit && !recipeUnitsMatch(query.data.unit, item.unit);
  const sourceRecipe = item.quoteCostRecipe ?? (remoteRecipe?.success ? remoteRecipe.data : null);
  const sectionContext = `${item.name} ${item.description ?? ""} ${item.sourceVariant?.name ?? ""} ${item.sourceVariant?.specification ?? ""}`;
  const computed = useMemo(() => {
    if (!sourceRecipe || (!item.quoteCostRecipe && unitMismatch)) return null;
    const dimensions = ensureLengthDimensions(item.quantityDerivation, item.unit, item.quantity, sectionContext, sourceRecipe, { itemName: item.name });
    try { return { recipe: section === "materials" && !purchasingManaged && hasDimensionPricing(sourceRecipe)
      ? priceRecipeFromDimensions(sourceRecipe, Number(item.unitCost), dimensions, sectionContext, item.quantity, item.unit)
      : item.quoteCostRecipe
      ? recipeAtQuotePrice(sourceRecipe, Number(item.unitCost))
        : priceRecipeFromDimensions(sourceRecipe, Number(item.unitCost), dimensions, sectionContext, item.quantity, item.unit), error: null }; }
    catch (error) { return { recipe: null, error: error instanceof Error ? error.message : "Unable to match this item price." }; }
  }, [sourceRecipe, item.unitCost, item.unit, item.quantity, item.quoteCostRecipe, item.quantityDerivation, sectionContext, unitMismatch, section, purchasingManaged]);
  // Copy once into this quote. Changes in the library after this point cannot
  // silently change this item's breakdown or a saved quote.
  useEffect(() => {
    // A managed materials view must not attach a competing library snapshot when opened.
    if (!item.quoteCostRecipe && computed?.recipe && (!purchasingManaged || section !== "materials")) onApply(computed.recipe);
    if (section && (!purchasingManaged || section !== "materials") && item.quoteCostRecipe && computed?.recipe) {
      const split = quoteRecipeSplit(computed.recipe, Number(item.quantity) || 0);
      if (Number(deriveCostRecipe(item.quoteCostRecipe).rateTypical) !== Number(item.unitCost)
        || (section === "materials" && !purchasingManaged && JSON.stringify(deriveCostRecipe(item.quoteCostRecipe).recipe) !== JSON.stringify(computed.recipe))
        || (item.labourCost != null && Math.round(item.labourCost * 100) !== Math.round(split.labourCost * 100))
        || (item.materialCost != null && Math.round(item.materialCost * 100) !== Math.round(split.materialCost * 100))
        || (item.plantCost != null && Math.round(item.plantCost * 100) !== Math.round(split.plantCost * 100))) {
        onApply(computed.recipe);
      }
    }
  }, [section, item.quoteCostRecipe, item.unitCost, item.quantity, item.labourCost, item.materialCost, item.plantCost, computed?.recipe, onApply, purchasingManaged]);
  const fmt = (n: string | number) => `£${Number(n).toFixed(2)}`;
  const icons = { labour: HardHat, materials: StackedBricksIcon, plant: TowerCraneIcon };
  const key = `quote-${index}`;
  const allowance = section && <QuoteSectionAllowance key={section} section={section} item={item}
    index={index} symbol={symbol} onUpdate={onSplitChange} />;
  return (
    <div className="space-y-2 min-w-0" data-testid={`quote-detail-breakdown-${index}`}>
      <div className="flex flex-nowrap items-center gap-1 sm:gap-2" data-testid={`quote-breakdown-actions-${index}`}>
        {(["labour", "materials", "plant"] as const).map(s => {
          const Icon = icons[s];
          return <button key={s} type="button" title={`${s[0].toUpperCase()}${s.slice(1)} details`}
            aria-label={`${s[0].toUpperCase()}${s.slice(1)} details`} aria-pressed={section === s}
            onClick={() => {
              const nextSection = section === s ? null : s;
              setSection(nextSection);
              setEditing(false);
              onSectionChange?.(nextSection !== null);
            }}
            className={`h-6 w-6 sm:h-8 sm:w-8 shrink-0 rounded-md border inline-flex items-center justify-center transition-colors focus-visible:ring-2 focus-visible:ring-[#5aabbf] ${section === s ? "border-[#5aabbf] bg-[#5aabbf]/15 text-[#5aabbf]" : "border-[#5aabbf]/40 bg-card text-[#5aabbf] hover:bg-[#5aabbf]/10"}`}
            data-testid={`button-quote-${s}-${index}`}><Icon className="h-4 w-4" /></button>;
        })}
        {action && <div className="ml-auto shrink-0">{action}</div>}
      </div>
      {section && (
        <div className="min-w-0 rounded-md border bg-card/50 p-3">
          {guestMode ? allowance
            : market !== "uk" ? allowance
            : section === "materials" && purchasingManaged && !editing &&
              (!item.quoteCostRecipe || !!purchaseSnapshot || !recipeProcurementSafe({ ...item, name: item.name ?? "" })) ? <div>
                <PurchaseMaterialSummary line={{ ...item, name: item.name ?? "", category: item.category ?? "general" }} />
                {item.quoteCostRecipe && <button type="button" className="text-xs text-primary mt-2"
                  onClick={() => setEditing(true)}>Edit recipe</button>}
              </div>
            : !taskId || taskId === "added" || taskId === "published" ?
              allowance
            : query.isPending && !item.quoteCostRecipe ? <p className="text-sm text-muted-foreground">Loading breakdown…</p>
            : query.isError && !item.quoteCostRecipe ? <><p role="alert" className="text-sm text-destructive mb-3">Could not load this Cost Library breakdown. Showing this quote’s allocation instead.</p>{allowance}</>
            : unitMismatch && !item.quoteCostRecipe ? <><p role="alert" className="text-sm text-muted-foreground mb-3">This library breakdown is priced per {query.data?.unit}, but the quote is priced per {item.unit}. Detailed quantities cannot be reused across these different units. Showing this quote’s allocation instead.</p>{allowance}</>
            : computed?.error ? <><p role="alert" className="text-sm text-destructive mb-3">{computed.error}</p>{allowance}</>
            : remoteRecipe && !remoteRecipe.success ? <><p role="alert" className="text-sm text-destructive mb-3">The Cost Library breakdown is invalid. Showing this quote’s allocation instead.</p>{allowance}</>
            : !computed?.recipe ? allowance
            : editing ? <CostBreakdownEdit key={`${key}-${section}`} taskId={key} section={section} unitLabel={item.unit || "item"}
                initial={computed.recipe} currentTypical={deriveCostRecipe(computed.recipe).rateTypical}
                formatCurrency={fmt} isSaving={false} isFirstAdd={false} onCancel={() => setEditing(false)}
                onSave={recipe => { onApply(manualRecipePrice(recipe)); setEditing(false); }} />
            : <CostBreakdownView taskId={key} section={section} unitLabel={item.unit || "item"}
                recipe={computed.recipe} rateTypical={deriveCostRecipe(computed.recipe).rateTypical}
                sourceLabel={item.quoteCostRecipe ? "Your version" : "Default"} formatCurrency={fmt}
                onEdit={() => setEditing(true)} showAssumptions={false}
                lineQuantity={Number(item.quantity) || 0}
                lineTotals={quoteRecipeSplit(computed.recipe, Number(item.quantity) || 0)} />}
          {section === "materials" && !guestMode && market === "uk" && sessionKey && onMaterialAllowanceChange &&
            <MaterialLookupControl line={{ ...item, name: item.name ?? "", category: item.category ?? "general" }}
              sessionKey={sessionKey} onChange={onMaterialAllowanceChange} />}
        </div>
      )}
    </div>
  );
}
